import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from './cx.ts';

/**
 * Right-side sheet for detail/edit flows (Radix Dialog under the hood).
 * Replaces the page-local one-off drawers scattered across the console.
 */
export type DrawerWidth = 'sm' | 'md' | 'lg' | 'xl';

const WIDTH_CLASSES: Record<DrawerWidth, string> = {
  sm: 'w-[360px]',
  md: 'w-[440px]',
  lg: 'w-[560px]',
  // Full detail sheets with embedded editable tables (e.g. the tenant-routing
  // config sheet, epic #886) — matches Tailwind's max-w-4xl measure.
  xl: 'w-[896px]',
};

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly width?: DrawerWidth;
  /** Accessible name for the close button — pass a translated string. */
  readonly closeLabel: string;
  readonly testId?: string;
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'md',
  closeLabel,
  testId,
}: Props): JSX.Element {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 animate-overlay-in" />
        <DialogPrimitive.Content
          data-testid={testId}
          className={cx(
            'fixed inset-y-0 right-0 z-50 flex max-w-[92vw] flex-col border-l border-line bg-card shadow-overlay animate-drawer-in focus:outline-none',
            WIDTH_CLASSES[width],
          )}
        >
          <div className="flex items-start gap-3 border-b border-line-soft px-5 py-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <DialogPrimitive.Title className="m-0 text-title text-paper">
                {title}
              </DialogPrimitive.Title>
              {description !== undefined && description !== null ? (
                <DialogPrimitive.Description className="m-0 text-dense text-paper-mid">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close
              aria-label={closeLabel}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-paper-low transition-colors duration-120 hover:bg-active hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer !== undefined && footer !== null ? (
            <div className="flex items-center justify-end gap-2 border-t border-line-soft px-5 py-3.5">
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
